import React, { useState } from 'react';
import { Stack, Typography, Button, Input, Box } from '@mui/material';
import { ArticleOutlined, FileUploadOutlined } from '@mui/icons-material';
import { FeedbackCircle as FileUploadContainer } from '@/components/shared/FeedbackCircle';
import { FileTypeModal } from './Modals/FileTypeModal';
import { TemplatesModal } from './Modals/TemplatesModal';
import { ConfirmationDialog as FileErrorDialog } from '@/components/shared/ConfirmationDialog';

type Props = {
  templatesModal: boolean;
  fileTypeModal: boolean;
  showListing: boolean;
  fileInputReference: React.MutableRefObject<HTMLInputElement | null | undefined>;
  setTemplatesModal: React.Dispatch<React.SetStateAction<boolean>>;
  setFileTypeModal: React.Dispatch<React.SetStateAction<boolean>>;
};

type FileError = { title: string; description: string };
const fileErrors = {
  typeError: {
    title: 'Incorrect File format',
    description: 'You have uploaded the incorrect file type. Please upload a .xlsx file.',
  },
  sizeError: {
    title: 'File size exceeded',
    description:
      'The file size exceeds the limit of 50MB. Please upload a file with a size less than 50MB.',
  },
};

export const UploadFile = ({
  setTemplatesModal,
  setFileTypeModal,
  templatesModal,
  fileTypeModal,
  fileInputReference,
  showListing,
}: Props) => {
  const [selectedFile, setSelectedFile] = useState<File | undefined>(undefined);
  const [fileError, setFileError] = useState<FileError | undefined>(undefined);

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];

    if (!file) return;

    const isXLSX = file.name.endsWith('.xlsx');
    const fileSize = file.size / (1024 * 1024);
    const isError = !isXLSX || fileSize > 50;
    if (isError) {
      setFileError(isXLSX ? fileErrors.sizeError : fileErrors.typeError);
      return;
    }
    setSelectedFile(file);
    setFileTypeModal(true);
    const fileInput = event.target;
    fileInput.value = '';
  };

  const handleTypeErrorDialog = () => {
    setFileError(undefined);
    if (fileInputReference.current) {
      const fileInput = fileInputReference.current;
      fileInput.value = '';
    }
  };

  return (
    <Box sx={{ display: showListing ? 'none' : 'block' }}>
      <Stack data-testid="top-title">
        <Typography color="text.primary" component="h1" fontSize="3.2rem" fontWeight={700}>
          Welcome to Fabric IDMS Sync
        </Typography>
        <Typography variant="body1" fontSize="1.6rem">
          You can upload and download IDMS files here.
        </Typography>
      </Stack>
      <Stack alignItems="center" data-testid="file-upload-container">
        <FileUploadContainer
          text={
            <span style={{ fontSize: '1.6rem' }}>
              Upload your file here, or download{' '}
              <a
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  setTemplatesModal(true);
                }}
                style={{ fontWeight: 'bold', color: '#1976d2' }}
              >
                XLSX templates
              </a>
            </span>
          }
          icon={<ArticleOutlined sx={{ color: 'text.secondary', fontSize: '7rem' }} />}
          backgroundColor="#F8FAFD"
          actionButton={
            <Button
              sx={{ backgroundColor: 'text.primary', color: 'white' }}
              variant="contained"
              size="large"
              startIcon={<FileUploadOutlined />}
              onClick={() => fileInputReference.current?.click()}
            >
              UPLOAD FILE
            </Button>
          }
        />
        <Input
          inputRef={fileInputReference}
          hidden
          type="file"
          id="upload-file"
          style={{ display: 'none' }}
          inputProps={{ accept: '.xlsx' }}
          onChange={handleFileSelect}
        />
      </Stack>

      <FileTypeModal
        title="Files type"
        description="Please select the file type you are uploading"
        open={fileTypeModal}
        file={selectedFile}
        handleClose={() => setFileTypeModal(false)}
        fileInputReference={fileInputReference}
      />
      {templatesModal && (
        <TemplatesModal
          open={templatesModal}
          handleClose={() => setTemplatesModal(false)}
          title="Templates"
          description="Please choose the template type you wish to download."
        />
      )}
      {fileError && (
        <FileErrorDialog
          title={fileError.title}
          description={fileError.description}
          confirmButtonText="Got It"
          onConfirm={handleTypeErrorDialog}
          handleClose={() => {}}
          isOpen={!!fileError}
        />
      )}
    </Box>
  );
};
